/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import { assert, object } from '@codegrade/utils';
import { UNSET_SENTINEL } from '@/constants';
/**
 * A key-value store for caching the result of expensive operations.
 *
 * @example
 * ```typescript
 * const expensiveOperation = () => {
 *     // This takes a while...
 *     return 0;
 * };
 * const myCache = new Cache(['key1', 'key2']);
 * // returns 0 after a while
 * myCache.get('key1', () => expensiveOperation());
 * // returns 0 instantly
 * myCache.get('key1', () => expensiveOperation());
 * ```
 */
class Cache {
    /** The mapping holding the cached values. */
    _cache;
    /**
     * @param keys The keys of the cache.
     */
    constructor(keys) {
        const obj = object.mapToObject(keys, key => [key, UNSET_SENTINEL]);
        this._cache = Object.seal(obj);
        Object.freeze(this);
    }
    /**
     * Get a value from the cache, or calculate and store it if not yet present.
     *
     * @param key The key to get.
     * @param ifNotPresent Called only if the requested key is not yet present.
     *     This function should be deterministic, as it will only be called once.
     * @returns The requested value.
     */
    get(key, ifNotPresent) {
        assert.AssertionError.assert(object.hasAttr(this._cache, key));
        if (this._cache[key] === UNSET_SENTINEL) {
            this._cache[key] = ifNotPresent(key);
        }
        return this._cache[key];
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function makeCache(...keys) {
    return new Cache(keys);
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
class Cacheable {
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class CacheDecorator {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _cache = new Map();
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor() {
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static cacheWith(cacheAttr) {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        function cache(_, name, descriptor) {
            const getter = descriptor.get;
            if (getter == null) {
                throw new TypeError('Getter property descriptor expected');
            }
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            function cachedGetter() {
                const cacheObj = this[cacheAttr];
                // eslint-disable-next-line no-underscore-dangle
                const lookup = cacheObj._cache;
                if (lookup.has(name)) {
                    return lookup.get(name);
                }
                const value = getter.call(this);
                lookup.set(name, value);
                return value;
            }
            descriptor.get = cachedGetter;
        }
        return cache;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static cache(target, name, descriptor) {
        return CacheDecorator.cacheWith('_cache')(target, name, descriptor);
    }
}
